import { GetAllBloggerResult, GetAllBloggersEvent } from 'bloggers-backend/lib/bloggers/domain';
import { GridColDef, GridColumnVisibilityModel, GridRowModel } from '@mui/x-data-grid';
import { Button } from '@mui/material';
import { Box } from '@mui/system';
import * as React from 'react';
import { useNavigate, Link } from 'react-router-dom';

import Grid, { QueryOptions } from './Grid';
import { AppContext } from '../AppContext';
import { FormatUserTime, useUserTime } from '../hooks/useUserTime';

function takeHost(number: number) {
  return (_value: unknown, row: GridRowModel) => row?.hosts?.[number]
}

const initialColumnVisibilityModel: GridColumnVisibilityModel = { youtube: false, youtubeChannelId: false, tikTok: false, website: false };

const buildColumns = (formatUserTime: FormatUserTime): GridColDef[] => [
  {
    field: 'name',
    width: 150,
    filterable: true,
    sortable: true,
    renderCell: ({row}: any) => (<Link to={`/bloggers/${row?.id}`}>{row?.name}</Link>)
  },
  { field: 'manager.name', headerName: 'manager', width: 150, filterable: true, sortable: true, valueGetter: (_value, row) => row?.manager?.name},
  { field: 'channelTheme', headerName: 'channel theme', width: 150, filterable: true, sortable: true },
  { field: 'status', width: 150, filterable: true, sortable: true },
  {
      field: 'email',
      width: 150,
      filterable: true,
      sortable: true,
      renderCell: ({row}: any) => (<a href={`https://mail.google.com/mail/?view=cm&source=mailto&to=${row?.email}`}>{row?.email}</a>)
  },
  { field: 'instagram', width: 150, filterable: true, sortable: false },
  { field: 'country', width: 150, filterable: true, sortable: true },
  { 
    field: 'createTime', 
    headerName: 'create time', 
    width: 300, 
    filterable: true, 
    sortable: true,
    valueGetter: (_value, row) => formatUserTime(row?.createTime)
  },
  {
      field: 'lastComment.text',
      headerName: 'last comment',
      width: 150,
      filterable: false,
      sortable: false,
      valueGetter: (_value, row) => row?.lastComment?.text
  },
  { 
    field: 'lastComment.date',
    headerName: 'last comment date',
    width: 300,
    filterable: true,
    sortable: true,
    valueGetter: (_value, row) => formatUserTime(row?.lastComment?.date)
  },
  { field: 'host1', headerName: 'host 1', width: 150, filterable: false, sortable: false, valueGetter: takeHost(0) },
  { field: 'host2', headerName: 'host 2', width: 150, filterable: false, sortable: false, valueGetter: takeHost(1) },
  { field: 'host3', headerName: 'host 3', width: 150, filterable: false, sortable: false, valueGetter: takeHost(2) },
  { field: 'youtube', width: 150, filterable: true, sortable: false },
  { field: 'youtubeChannelId', width: 150, filterable: true, sortable: false },
  { field: 'tikTok', width: 150, filterable: true, sortable: false },
  { field: 'website', width: 150, filterable: true, sortable: false },
];


export default function BloggersPage() {
  const {user, client, notificationsService} = React.useContext(AppContext);
  const navigate = useNavigate();
  const formatUserTime = useUserTime();
  const columns = React.useMemo(() => buildColumns(formatUserTime), [formatUserTime]);

  const fetchData = async (options: QueryOptions) => {
    const result = await client<GetAllBloggersEvent, GetAllBloggerResult>({
      event: 'getAllBloggers',
      data: options as any
    })

    if (result.success) {
      return result.value
    } else {
      notificationsService.notify({
        severity: 'error',
        message: `Error: ${JSON.stringify(result.errors)}`
      });

      return {data: [], pageInfo: {}}
    }
  };


  return <Box sx={{display: 'flex', flexDirection: 'column', height: '100%' }}>
    <Box sx={{marginBottom: 1}}>
      <Button
        variant="contained"
        onClick={() => navigate('/bloggers/new')}
      >
        CREATE NEW BLOGGER
      </Button>
      {
        user.role === "admin"
        ? (<Button
          variant="contained"
          onClick={() => navigate('/bloggers_report')}
          sx={{marginLeft: 1}}
        >
          Report
        </Button>)
        : <></>
      }
      {
        user.role === "admin"
        ? (<>
          <Button
            variant="contained"
            onClick={() => navigate('/bloggers_growth_report')}
            sx={{marginLeft: 1}}
          >
            Growth report
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/bloggers_follow_up_report')}
            sx={{marginLeft: 1}}
          >
            Follow-up report
          </Button>
        </>)
        : <></>
      }
    </Box>
    <Box sx={{flexGrow: 1}}>
      <Grid
        cacheName={'bloggers'}
        columns={columns}
        initialColumnVisibilityModel={initialColumnVisibilityModel}
        fetchData={fetchData}
        onRowClick={(id) => {}}
        showSearch={true}
      />
    </Box>
  </Box>
}
